import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import SearchComponent from '../../components/ui/SearchComponent';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ProblemCard from './components/ProblemCard';
import FilterPanel from './components/FilterPanel';
import ProblemDetailModal from './components/ProblemDetailModal';
import FavoritesPanel from './components/FavoritesPanel';

const ProblemStatementBrowse = () => {
  const navigate = useNavigate();
  const [problems, setProblems] = useState([]);
  const [filteredProblems, setFilteredProblems] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [appliedProblems, setAppliedProblems] = useState([]);
  const [selectedProblem, setSelectedProblem] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isFilterCollapsed, setIsFilterCollapsed] = useState(true);
  const [isFavoritesOpen, setIsFavoritesOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({
    searchTerm: '',
    cities: [],
    categories: [],
    timelines: [],
    skills: [],
    teamSizes: [],
    complexity: [],
    sortBy: 'relevance'
  });

  // Mock data for problems
  const mockProblems = [
    {
      id: 1,
      title: "Smart Traffic Management System",
      cityName: "Atlanta, GA",
      description: "Develop an AI-powered traffic management system to reduce congestion and improve traffic flow in downtown Atlanta during peak hours.",
      fullDescription: `Atlanta faces significant traffic congestion challenges, particularly during rush hours and major events. The city needs an intelligent traffic management system that can analyze real-time traffic patterns, predict congestion hotspots, and automatically adjust traffic signal timing to optimize flow.\n\nThe solution should integrate with existing traffic infrastructure and provide actionable insights to traffic management personnel. The system must be scalable and adaptable to different traffic scenarios including emergency vehicle prioritization and special event management.`,
      cityContext: "Atlanta is a major metropolitan hub with over 500,000 daily commuters. The downtown area experiences severe congestion during peak hours, resulting in economic losses and environmental impact.",
      complexity: "Intermediate",
      teamSize: "4-5",
      timeline: "3-4 months",
      deadline: "2025-02-15",
      techFocus: ["AI", "IoT", "Data Science"],
      requiredSkills: ["Python", "Machine Learning", "Data Analysis", "IoT Development", "API Integration", "Database Design"],
      deliverables: [
        { title: "Problem Brief", description: "Comprehensive analysis of current traffic patterns", dueDate: "Week 2" },
        { title: "System Architecture", description: "Technical design and implementation plan", dueDate: "Week 4" },
        { title: "Prototype", description: "Working prototype with core functionality", dueDate: "Week 8" },
        { title: "Final Presentation", description: "Demo and implementation roadmap", dueDate: "Week 12" }
      ],
      teamComposition: [
        { role: "Data Scientist", description: "Traffic pattern analysis and ML model development" },
        { role: "Software Developer", description: "System integration and API development" },
        { role: "IoT Specialist", description: "Sensor integration and hardware communication" },
        { role: "Project Manager", description: "Coordination and stakeholder communication" }
      ],
      prerequisites: [
        "Experience with Python and data analysis libraries",
        "Understanding of machine learning concepts",
        "Basic knowledge of IoT systems",
        "Familiarity with API development"
      ],
      successCriteria: [
        "Demonstrate 15% improvement in traffic flow efficiency",
        "Real-time processing of traffic data within 30 seconds",
        "Integration with at least 3 existing traffic systems",
        "Scalable architecture supporting city-wide deployment"
      ],
      cityContact: {
        name: "Sarah Johnson",
        title: "Smart City Technology Director",
        department: "Department of Transportation",
        email: "sarah.johnson@atlanta.gov",
        phone: "(404) 555-0123",
        officeHours: "Monday-Friday, 9:00 AM - 5:00 PM EST"
      },
      cityInfo: "Atlanta is committed to becoming a leading smart city through innovative technology solutions. Our transportation department actively collaborates with universities and tech companies to address urban mobility challenges.",
      category: "Transportation",
      submittedDate: "2025-01-05"
    },
    {
      id: 2,
      title: "Water Quality Monitoring Network",
      cityName: "Birmingham, AL",
      description: "Create an IoT-based water quality monitoring system to track contamination levels and ensure safe drinking water across the city.",
      fullDescription: `Birmingham needs a comprehensive water quality monitoring system that can provide real-time data on water safety parameters across the municipal water supply network. The system should detect contamination events quickly and alert authorities to potential health risks.\n\nThe solution must integrate multiple sensor types, provide data visualization dashboards, and include predictive analytics to identify potential issues before they become critical. The system should be cost-effective and maintainable by city staff.`,
      cityContext: "Birmingham serves over 200,000 residents with municipal water. Recent infrastructure aging has created concerns about water quality consistency across different neighborhoods.",
      complexity: "Beginner",
      teamSize: "3-4",
      timeline: "2-3 months",
      deadline: "2025-03-01",
      techFocus: ["IoT", "Data Science"],
      requiredSkills: ["IoT Development", "Sensor Integration", "Data Visualization", "Database Management", "Web Development"],
      deliverables: [
        { title: "System Design", description: "IoT architecture and sensor placement plan", dueDate: "Week 2" },
        { title: "Prototype Network", description: "Small-scale monitoring system", dueDate: "Week 6" },
        { title: "Dashboard Interface", description: "Real-time monitoring dashboard", dueDate: "Week 8" },
        { title: "Implementation Guide", description: "Deployment and maintenance documentation", dueDate: "Week 10" }
      ],
      teamComposition: [
        { role: "IoT Developer", description: "Sensor network design and implementation" },
        { role: "Data Analyst", description: "Data processing and visualization" },
        { role: "Web Developer", description: "Dashboard and user interface development" }
      ],
      prerequisites: [
        "Basic understanding of IoT systems",
        "Experience with sensor technologies",
        "Knowledge of web development frameworks",
        "Understanding of database systems"
      ],
      successCriteria: [
        "Monitor at least 10 key water quality parameters",
        "Provide real-time alerts within 5 minutes of detection",
        "Achieve 99% system uptime",
        "User-friendly dashboard for non-technical staff"
      ],
      cityContact: {
        name: "Michael Davis",
        title: "Water Systems Manager",
        department: "Birmingham Water Works",
        email: "michael.davis@bwwb.org",
        phone: "(205) 555-0456",
        officeHours: "Monday-Friday, 8:00 AM - 4:30 PM CST"
      },
      cityInfo: "Birmingham is investing in smart infrastructure to ensure reliable public services. Our water department is particularly focused on leveraging technology for proactive maintenance and quality assurance.",
      category: "Water Systems",
      submittedDate: "2025-01-08"
    },
    {
      id: 3,
      title: "Smart Waste Collection Optimization",
      cityName: "Charlotte, NC",
      description: "Design an intelligent waste collection system using IoT sensors to optimize pickup routes and reduce operational costs.",
      fullDescription: `Charlotte's waste management department seeks to modernize collection operations through smart technology. The current system relies on fixed schedules that often result in inefficient routes and unnecessary trips to partially full containers.\n\nThe proposed solution should use IoT sensors to monitor fill levels in waste containers, optimize collection routes dynamically, and provide predictive analytics for maintenance scheduling. The system must integrate with existing fleet management software and provide clear ROI metrics.`,
      cityContext: "Charlotte manages waste collection for over 280,000 households. The city aims to reduce collection costs by 20% while improving service reliability and environmental impact.",
      complexity: "Advanced",
      teamSize: "5-6",
      timeline: "4-5 months",
      deadline: "2025-01-30",
      techFocus: ["IoT", "AI", "GIS"],
      requiredSkills: ["IoT Development", "Machine Learning", "GIS Mapping", "Route Optimization", "Mobile Development", "Data Analytics"],
      deliverables: [
        { title: "Current State Analysis", description: "Assessment of existing waste collection operations", dueDate: "Week 3" },
        { title: "IoT Sensor Network", description: "Smart bin monitoring system", dueDate: "Week 6" },
        { title: "Route Optimization Algorithm", description: "AI-powered route planning system", dueDate: "Week 10" },
        { title: "Mobile Application", description: "Driver interface and management dashboard", dueDate: "Week 14" },
        { title: "Pilot Implementation", description: "Small-scale deployment and testing", dueDate: "Week 18" }
      ],
      teamComposition: [
        { role: "IoT Engineer", description: "Sensor network and hardware integration" },
        { role: "Data Scientist", description: "Route optimization and predictive analytics" },
        { role: "GIS Specialist", description: "Mapping and spatial analysis" },
        { role: "Mobile Developer", description: "Driver app and user interfaces" },
        { role: "Project Manager", description: "Stakeholder coordination and timeline management" }
      ],
      prerequisites: [
        "Advanced programming skills in Python or Java",
        "Experience with IoT platforms and sensors",
        "Knowledge of machine learning algorithms",
        "Understanding of GIS systems and mapping",
        "Mobile app development experience"
      ],
      successCriteria: [
        "Reduce collection route distance by 25%",
        "Decrease operational costs by 20%",
        "Improve collection efficiency by 30%",
        "Achieve 95% sensor reliability",
        "Positive ROI within 18 months"
      ],
      cityContact: {
        name: "Jennifer Martinez",
        title: "Waste Management Innovation Director",
        department: "Charlotte Department of Solid Waste Services",
        email: "jennifer.martinez@charlottenc.gov",
        phone: "(704) 555-0789",
        officeHours: "Tuesday-Thursday, 10:00 AM - 6:00 PM EST"
      },
      cityInfo: "Charlotte is recognized as a leader in municipal innovation. Our solid waste department actively seeks technology partnerships to improve service delivery and environmental sustainability.",
      category: "Waste Management",
      submittedDate: "2025-01-03"
    },
    {
      id: 4,
      title: "Public Safety Analytics Platform",
      cityName: "Memphis, TN",
      description: "Build a data analytics platform to help police departments predict crime patterns and allocate resources more effectively.",
      fullDescription: `Memphis Police Department needs a comprehensive analytics platform that can process multiple data sources to identify crime patterns, predict hotspots, and optimize patrol deployment. The system should respect privacy concerns while providing actionable insights for crime prevention.\n\nThe platform must integrate with existing police databases, emergency services, and community reporting systems. It should provide both real-time alerts and long-term trend analysis to support strategic planning and tactical operations.`,
      cityContext: "Memphis has experienced varying crime rates across different neighborhoods. The police department seeks data-driven approaches to improve public safety while building community trust.",
      complexity: "Advanced",
      teamSize: "4-5",
      timeline: "3-4 months",
      deadline: "2025-02-28",
      techFocus: ["AI", "Data Science"],
      requiredSkills: ["Data Science", "Machine Learning", "Statistical Analysis", "Database Management", "Data Visualization", "Privacy Protection"],
      deliverables: [
        { title: "Data Integration Framework", description: "Secure data collection and processing system", dueDate: "Week 4" },
        { title: "Predictive Models", description: "Crime pattern analysis and forecasting algorithms", dueDate: "Week 8" },
        { title: "Analytics Dashboard", description: "Interactive visualization and reporting interface", dueDate: "Week 10" },
        { title: "Privacy Compliance Report", description: "Data protection and ethical use guidelines", dueDate: "Week 12" }
      ],
      teamComposition: [
        { role: "Data Scientist", description: "Predictive modeling and statistical analysis" },
        { role: "Software Engineer", description: "Platform development and database integration" },
        { role: "Privacy Specialist", description: "Data protection and compliance oversight" },
        { role: "UX Designer", description: "Dashboard design and user experience" }
      ],
      prerequisites: [
        "Advanced knowledge of machine learning and statistics",
        "Experience with large-scale data processing",
        "Understanding of privacy and ethical considerations",
        "Familiarity with law enforcement data systems"
      ],
      successCriteria: [
        "Achieve 80% accuracy in crime pattern prediction",
        "Reduce response time by 15%",
        "Improve resource allocation efficiency by 25%",
        "Full compliance with privacy regulations",
        "User adoption rate above 85%"
      ],
      cityContact: {
        name: "Captain Robert Wilson",
        title: "Technology and Innovation Commander",
        department: "Memphis Police Department",
        email: "robert.wilson@memphistn.gov",
        phone: "(901) 555-0234",
        officeHours: "Monday-Friday, 7:00 AM - 3:00 PM CST"
      },
      cityInfo: "Memphis is committed to using technology responsibly to enhance public safety. Our police department prioritizes community engagement and data-driven decision making.",
      category: "Public Safety",
      submittedDate: "2025-01-10"
    },
    {
      id: 5,
      title: "Digital Infrastructure Assessment Tool",
      cityName: "Nashville, TN",
      description: "Develop a comprehensive tool to assess and map the city's digital infrastructure capabilities and identify improvement opportunities.",
      fullDescription: `Nashville needs a systematic approach to evaluate its digital infrastructure across all city departments and public services. The tool should assess current capabilities, identify gaps, and provide recommendations for strategic technology investments.\n\nThe assessment should cover network infrastructure, cybersecurity posture, digital service delivery, and citizen engagement platforms. The tool must be user-friendly for non-technical staff and provide clear metrics for decision-making.`,
      cityContext: "Nashville is rapidly growing and needs to ensure its digital infrastructure can support increased demand for city services and smart city initiatives.",complexity: "Intermediate",teamSize: "3-4",timeline: "2-3 months",deadline: "2025-03-15",
      techFocus: ["Digital Infrastructure", "Data Science"],
      requiredSkills: ["System Analysis", "Network Assessment", "Cybersecurity", "Data Visualization", "Technical Writing"],
      deliverables: [
        { title: "Assessment Framework", description: "Comprehensive evaluation methodology", dueDate: "Week 3" },
        { title: "Infrastructure Audit", description: "Current state analysis and gap identification", dueDate: "Week 6" },
        { title: "Recommendation Report", description: "Strategic improvement plan with priorities", dueDate: "Week 8" },
        { title: "Assessment Tool", description: "Reusable evaluation platform for ongoing monitoring", dueDate: "Week 10" }
      ],
      teamComposition: [
        { role: "Systems Analyst", description: "Infrastructure assessment and technical evaluation" },
        { role: "Cybersecurity Specialist", description: "Security posture analysis and recommendations" },
        { role: "Data Analyst", description: "Metrics development and visualization" }
      ],
      prerequisites: [
        "Understanding of IT infrastructure and networking","Knowledge of cybersecurity best practices","Experience with system assessment methodologies","Strong analytical and communication skills"
      ],
      successCriteria: [
        "Complete assessment of all major city systems","Identify top 10 improvement priorities","Provide actionable recommendations with cost estimates","Create reusable assessment framework","Achieve stakeholder buy-in for recommendations"
      ],
      cityContact: {
        name: "Lisa Thompson",title: "Chief Information Officer",department: "Nashville Information Technology Services",email: "lisa.thompson@nashville.gov",phone: "(615) 555-0567",officeHours: "Monday-Friday, 9:00 AM - 5:00 PM CST"
      },
      cityInfo: "Nashville is investing heavily in digital transformation to support its growing population and economic development. Our IT department leads smart city initiatives across all municipal services.",category: "Digital Infrastructure",submittedDate: "2025-01-12"
    },
    {
      id: 6,
      title: "Environmental Monitoring Dashboard",cityName: "New Orleans, LA",description: "Create a real-time environmental monitoring system to track air quality, noise levels, and weather conditions across the city.",
      fullDescription: `New Orleans faces unique environmental challenges including air quality concerns, noise pollution, and extreme weather events. The city needs a comprehensive monitoring system that can track multiple environmental parameters and provide early warnings for health and safety concerns.\n\nThe dashboard should integrate data from various sensors and external sources, provide public access to environmental data, and support regulatory compliance reporting. The system must be resilient to extreme weather conditions common in the region.`,
      cityContext: "New Orleans has specific environmental challenges due to its geographic location, industrial activity, and climate. The city prioritizes environmental health and climate resilience.",complexity: "Beginner",teamSize: "3-4",timeline: "2-3 months",deadline: "2025-04-01",
      techFocus: ["IoT", "Data Science", "Environmental Monitoring"],
      requiredSkills: ["IoT Development", "Data Visualization", "Environmental Science", "Web Development", "API Integration"],
      deliverables: [
        { title: "Sensor Network Design", description: "Environmental monitoring infrastructure plan", dueDate: "Week 3" },
        { title: "Data Collection System", description: "Automated data gathering and processing", dueDate: "Week 6" },
        { title: "Public Dashboard", description: "Citizen-facing environmental data portal", dueDate: "Week 8" },
        { title: "Alert System", description: "Automated notifications for environmental hazards", dueDate: "Week 10" }
      ],
      teamComposition: [
        { role: "IoT Developer", description: "Sensor integration and data collection" },
        { role: "Web Developer", description: "Dashboard and public interface development" },
        { role: "Environmental Specialist", description: "Data interpretation and alert thresholds" }
      ],
      prerequisites: [
        "Basic understanding of environmental monitoring","Experience with IoT sensors and data collection","Web development skills","Knowledge of data visualization techniques"
      ],
      successCriteria: [
        "Monitor at least 8 environmental parameters","Provide real-time data updates every 15 minutes","Achieve 98% system uptime","Public dashboard with 1000+ monthly users","Compliance with EPA reporting requirements"
      ],
      cityContact: {
        name: "Dr. Maria Rodriguez",title: "Environmental Health Director",department: "New Orleans Health Department",email: "maria.rodriguez@nola.gov",phone: "(504) 555-0890",officeHours: "Monday-Friday, 8:30 AM - 4:30 PM CST"
      },
      cityInfo: "New Orleans is committed to environmental sustainability and public health protection. Our health department works closely with community organizations to address environmental justice concerns.",category: "Environmental Monitoring",submittedDate: "2025-01-15"
    }
  ];

  useEffect(() => {
    // Simulate loading
    setTimeout(() => {
      setProblems(mockProblems);
      setFilteredProblems(mockProblems);
      setIsLoading(false);
    }, 1000);
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, problems]);

  const applyFilters = () => {
    let filtered = [...problems];

    // Search term filter
    if (filters?.searchTerm) {
      const searchLower = filters?.searchTerm?.toLowerCase();
      filtered = filtered?.filter(problem =>
        problem?.title?.toLowerCase()?.includes(searchLower) ||
        problem?.description?.toLowerCase()?.includes(searchLower) ||
        problem?.cityName?.toLowerCase()?.includes(searchLower) ||
        problem?.requiredSkills?.some(skill => skill?.toLowerCase()?.includes(searchLower))
      );
    }

    // City filter
    if (filters?.cities?.length > 0) {
      filtered = filtered?.filter(problem => filters?.cities?.includes(problem?.cityName));
    }

    // Category filter
    if (filters?.categories?.length > 0) {
      filtered = filtered?.filter(problem => filters?.categories?.includes(problem?.category));
    }

    // Timeline filter
    if (filters?.timelines?.length > 0) {
      filtered = filtered?.filter(problem => filters?.timelines?.includes(problem?.timeline));
    }

    // Skills filter
    if (filters?.skills?.length > 0) {
      filtered = filtered?.filter(problem =>
        filters?.skills?.some(skill => problem?.requiredSkills?.includes(skill))
      );
    }

    // Team size filter
    if (filters?.teamSizes?.length > 0) {
      filtered = filtered?.filter(problem => filters?.teamSizes?.includes(problem?.teamSize + ' members'));
    }

    // Complexity filter
    if (filters?.complexity?.length > 0) {
      filtered = filtered?.filter(problem => filters?.complexity?.includes(problem?.complexity));
    }

    // Sorting
    switch (filters?.sortBy) {
      case 'deadline':
        filtered?.sort((a, b) => new Date(a.deadline) - new Date(b.deadline));
        break;
      case 'complexity':
        const complexityOrder = { 'Beginner': 1, 'Intermediate': 2, 'Advanced': 3 };
        filtered?.sort((a, b) => complexityOrder?.[a?.complexity] - complexityOrder?.[b?.complexity]);
        break;
      case 'newest':
        filtered?.sort((a, b) => new Date(b.submittedDate) - new Date(a.submittedDate));
        break;
      case 'oldest':
        filtered?.sort((a, b) => new Date(a.submittedDate) - new Date(b.submittedDate));
        break;
      default:
        // Relevance - keep original order
        break;
    }

    setFilteredProblems(filtered);
  };

  const handleSearch = (searchTerm) => {
    setFilters(prev => ({ ...prev, searchTerm }));
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleFavorite = (problemId) => {
    const problem = problems?.find(p => p?.id === problemId);
    if (favorites?.find(f => f?.id === problemId)) {
      setFavorites(prev => prev?.filter(f => f?.id !== problemId));
    } else {
      setFavorites(prev => [...prev, problem]);
    }
  };

  const handleApply = (problemId) => {
    if (!appliedProblems?.includes(problemId)) {
      setAppliedProblems(prev => [...prev, problemId]);
      // In a real app, this would make an API call
      console.log('Applied to problem:', problemId);
    }
  };

  const handleViewDetails = (problemId) => {
    const problem = problems?.find(p => p?.id === problemId);
    setSelectedProblem(problem);
    setIsDetailModalOpen(true);
  };

  const handleRemoveFavorite = (problemId) => {
    setFavorites(prev => prev?.filter(f => f?.id !== problemId));
  };

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/student-dashboard' },
    { label: 'Problem Statements' }
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header userRole="student" userName="Alex Johnson" notifications={3} />
        <div className="flex items-center justify-center h-96">
          <div className="flex items-center space-x-3">
            <Icon name="Loader2" size={24} className="animate-spin text-primary" />
            <span className="text-foreground">Loading problems...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header userRole="student" userName="Alex Johnson" notifications={3} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Breadcrumb items={breadcrumbItems} />
        
        {/* Page Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">Problem Statements</h1>
            <p className="text-muted-foreground">
              Discover city challenges and apply your skills to real-world problems
            </p>
          </div>
          <div className="flex items-center space-x-3 mt-4 lg:mt-0">
            <SearchComponent
              placeholder="Search problems, cities, or skills..."
              onSearch={handleSearch}
              searchScope="problems"
              className="w-full lg:w-80"
            />
            <Button
              variant="outline"
              onClick={() => navigate('/team-formation-hub')}
              iconName="Users"
              iconPosition="left"
            >
              Find Team
            </Button>
          </div>
        </div>

        {/* Results Summary */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <span className="text-foreground font-medium">
              {filteredProblems?.length} problems found
            </span>
            {filters?.searchTerm && (
              <span className="text-muted-foreground text-sm">
                for "{filters?.searchTerm}"
              </span>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsFilterCollapsed(!isFilterCollapsed)}
              iconName="Filter"
              iconPosition="left"
              className="lg:hidden"
            >
              Filters
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex gap-8">
          {/* Filter Panel */}
          <div className={`${isFilterCollapsed ? 'hidden' : 'block'} lg:block w-full lg:w-80 flex-shrink-0`}>
            <FilterPanel
              filters={filters}
              onFiltersChange={handleFiltersChange}
              resultCount={filteredProblems?.length}
              isCollapsed={isFilterCollapsed && window.innerWidth < 1024}
              onToggleCollapse={() => setIsFilterCollapsed(!isFilterCollapsed)}
            />
          </div>

          {/* Problem Cards */}
          <div className="flex-1">
            {filteredProblems?.length === 0 ? (
              <div className="text-center py-12">
                <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">No problems found</h3>
                <p className="text-muted-foreground mb-4">
                  Try adjusting your filters or search terms
                </p>
                <Button
                  variant="outline"
                  onClick={() => handleFiltersChange({
                    searchTerm: '',
                    cities: [],
                    categories: [],
                    timelines: [],
                    skills: [],
                    teamSizes: [],
                    complexity: [],
                    sortBy: 'relevance'
                  })}
                >
                  Clear All Filters
                </Button>
              </div>
            ) : (
              <div className="space-y-6">
                {filteredProblems?.map((problem) => (
                  <ProblemCard
                    key={problem?.id}
                    problem={problem}
                    onApply={handleApply}
                    onFavorite={handleFavorite}
                    onViewDetails={handleViewDetails}
                    isFavorited={favorites?.some(f => f?.id === problem?.id)}
                    hasApplied={appliedProblems?.includes(problem?.id)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Problem Detail Modal */}
      <ProblemDetailModal
        problem={selectedProblem}
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        onApply={handleApply}
        hasApplied={selectedProblem && appliedProblems?.includes(selectedProblem?.id)}
      />
      {/* Favorites Panel */}
      <FavoritesPanel
        favorites={favorites}
        onRemoveFavorite={handleRemoveFavorite}
        onViewProblem={handleViewDetails}
        isOpen={isFavoritesOpen}
        onToggle={() => setIsFavoritesOpen(!isFavoritesOpen)}
      />
    </div>
  );
};

export default ProblemStatementBrowse;